// Turn Link Comments into Links (design-docs/Links — design.md §5; the Mac's `LibraryModel.convertLinkComments` and
// `offerLinkConversionIfNeeded`): links that importers kept as margin comments before the dialect had links become
// links on the words they mark, after a "Before turning link comments into links" checkpoint. The project menu offers it
// always; a project that has any is offered it once in this browser (localStorage `ws:linkCommentsOffered:<project>`).
import type * as A from '@automerge/automerge';
import type { OpenedProject, Project } from '@writing-suite/core';
import { linkCommentCount as countIn } from '@writing-suite/core/revise';
import type { LiveProject } from './project.ts';
import { reviseEdit } from './revise.ts';
import { storageId } from './storageId.ts';

/** How many of the project's link comments would become links (its prose documents as they are now). */
export function linkCommentCount(live: LiveProject | null, project: Project): number {
  const opened = project as Partial<OpenedProject>;
  if (typeof opened.fragmentIds !== 'function') return 0;
  const docs = opened.fragmentIds().flatMap((id) => {
    const doc = live?.doc(`fragment.${id}`) ?? opened.fragmentDoc?.(id) ?? null;
    return doc ? [doc as A.Doc<unknown>] : [];
  });
  return countIn(docs);
}

/** Turns them into links (locked documents passed by) and resolves the comments; an open editor follows the change.
 *  Returns how many. */
export function convertLinkComments(live: LiveProject): number {
  return reviseEdit(live, (r) => r.convertLinkComments());
}

/** What the writer is told afterwards. */
export function convertedMessage(count: number): string {
  if (count === 0) return 'No link comments could become links.';
  return `Turned ${count} link ${count === 1 ? 'comment' : 'comments'} into links.`;
}

const offeredKey = (projectId: string) => `ws:linkCommentsOffered:${storageId(projectId)}`;

/** Whether this browser has offered the conversion for this project (or can't remember: then it doesn't offer). */
export function linkCommentsOffered(projectId: string): boolean {
  try {
    return localStorage.getItem(offeredKey(projectId)) === '1';
  } catch {
    return true;
  }
}

export function markLinkCommentsOffered(projectId: string) {
  try {
    localStorage.setItem(offeredKey(projectId), '1');
  } catch {
    // offered again next time
  }
}
